import type { UUID } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';

interface UseRepositoryMetaResult {
  detectingRelationshipsCount: number;
  effectiveMissingMatchesCount: number;
  failedRelationshipsCount: number;
  /**
   * True until the first server counts arrive. The zero-defaulted counts
   * below are placeholders while this is true — consumers making decisions
   * on a count (not just displaying it) must wait this out, or a "no
   * documents" zero is indistinguishable from "not loaded yet".
   */
  loading: boolean;
  unconfirmedRelationshipsCount: number;
  unprocessedRelationshipDocumentsCount: number;
}

/**
 * Single hook for repository-level relationship pipeline counts.
 *
 * The subscription delivers fresh server counts that overwrite the cache
 * whenever a review action completes on the backend.
 */
export function useRepositoryMeta(repositoryId: UUID | null): UseRepositoryMetaResult {
  const client = useApolloClient();
  const { data } = Apollo.useGetRepositoryMetaQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !repositoryId,
    variables: { repositoryId: repositoryId ?? '' },
  });
  const serverMeta = data?.repositoryMeta ?? null;

  Apollo.useOnRepositoryMetaUpdatedSubscription({
    onData: ({ data }) => {
      if (!data.data?.onRepositoryMetaUpdated || !repositoryId) return;
      client.cache.writeQuery<Apollo.GetRepositoryMetaQuery>({
        data: {
          __typename: 'Query',
          repositoryMeta: data.data.onRepositoryMetaUpdated,
        },
        query: Apollo.GetRepositoryMetaDocument,
        variables: { repositoryId },
      });
    },
    skip: !repositoryId,
    variables: { repositoryId: repositoryId ?? '' },
  });

  return {
    detectingRelationshipsCount: serverMeta?.detectingRelationshipsCount ?? 0,
    effectiveMissingMatchesCount: serverMeta?.missingMatchesCount ?? 0,
    failedRelationshipsCount: serverMeta?.failedRelationshipsCount ?? 0,
    loading: !serverMeta,
    unconfirmedRelationshipsCount: serverMeta?.unconfirmedRelationshipsCount ?? 0,
    unprocessedRelationshipDocumentsCount: serverMeta?.unprocessedRelationshipDocumentsCount ?? 0,
  };
}
